import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  User, Building2, Users, ArrowRight, ArrowLeft, Check,
  Briefcase, GraduationCap, Trophy, Heart, Shield,
  Mail, Key, Search, Loader2, CheckCircle2, Clock,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User type cards configuration
const USER_TYPES = [
  {
    id: 'individual',
    title: 'Individual',
    description: 'Athletes, fans, coaches, parents, or agents',
    icon: User,
    color: 'from-blue-500 to-blue-700',
    roles: ['Athlete', 'Fan', 'Coach', 'Parent', 'Agent']
  },
  {
    id: 'organization_admin',
    title: 'Organization Admin',
    description: 'Create or manage a sports organization',
    icon: Building2,
    color: 'from-primary to-green-600',
    paths: ['Create new organization', 'Join existing organization']
  },
  {
    id: 'staff',
    title: 'Staff / Team Member',
    description: 'Join an organization via invitation or code',
    icon: Users,
    color: 'from-secondary to-orange-600',
    paths: ['Use invitation link', 'Enter access code', 'Request to join']
  }
];

// Individual role icons
const ROLE_ICONS = {
  Athlete: Trophy,
  Fan: Heart,
  Coach: GraduationCap,
  Parent: Users,
  Agent: Briefcase
};

// Organization type icons
const ORG_TYPE_ICONS = {
  Federation: Shield,
  Club: Trophy,
  Academy: GraduationCap,
  School: GraduationCap,
  Facility: Building2,
  Corporate: Briefcase,
  Government: Shield
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [onboardingFlow, setOnboardingFlow] = useState(null);
  
  // Form state
  const [userType, setUserType] = useState(null);
  const [individualRole, setIndividualRole] = useState(null);
  const [orgAdminPath, setOrgAdminPath] = useState(null); // 'create' or 'join'
  const [staffPath, setStaffPath] = useState(null); // 'invitation', 'code', 'request'
  
  // Organization form
  const [orgForm, setOrgForm] = useState({
    name: '',
    type: '',
    sport: '',
    country: '',
    city: '',
    description: ''
  });
  
  // Join organization
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [requestedRole, setRequestedRole] = useState('');
  
  // Access code / invitation
  const [accessCode, setAccessCode] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [codeValidation, setCodeValidation] = useState(null);
  const [invitationDetails, setInvitationDetails] = useState(null);

  // Check for invitation token in URL
  useEffect(() => {
    const token = searchParams.get('invitation');
    if (token) {
      setInvitationToken(token);
      setUserType('staff');
      setStaffPath('invitation');
      validateInvitation(token);
    }
  }, [searchParams, validateInvitation]);

  // Fetch onboarding config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/onboarding/config`);
        setConfig(response.data);
      } catch (error) {
        console.error('Failed to load config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Start onboarding flow when user type is selected
  const startOnboarding = async (type, role = null) => {
    if (!user) {
      toast.error('Please sign up first');
      navigate('/signup');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/start`, {
        user_id: user.id,
        user_type: type,
        individual_role: role
      });
      setOnboardingFlow(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to start onboarding');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Complete individual onboarding
  const completeIndividual = async () => {
    if (!onboardingFlow) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding/complete-individual?onboarding_id=${onboardingFlow.id}`);
      toast.success('Welcome to Michezonet!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  // Create organization
  const createOrganization = async () => {
    if (!onboardingFlow) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/create-organization`, {
        onboarding_id: onboardingFlow.id,
        ...orgForm
      });
      toast.success(`${response.data.organization_name} created! You are now ${response.data.role}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  // Search organizations
  const searchOrganizations = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/organizations/search?query=${query}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  // Submit join request
  const submitJoinRequest = async () => {
    if (!onboardingFlow || !selectedOrg) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/join-organization`, {
        onboarding_id: onboardingFlow.id,
        organization_id: selectedOrg.id,
        requested_role: requestedRole || null,
        message: joinMessage || null
      });
      toast.success('Join request submitted! Waiting for admin approval.');
      setStep(step + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // Validate access code
  const validateAccessCode = async (code) => {
    if (code.length < 4) {
      setCodeValidation(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/access-codes/validate/${code}`);
      setCodeValidation(response.data);
    } catch (error) {
      setCodeValidation({ valid: false, error: 'Invalid code' });
    }
  };

  // Use access code
  const useAccessCode = async () => {
    if (!onboardingFlow || !codeValidation?.valid) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/use-access-code`, {
        onboarding_id: onboardingFlow.id,
        code: accessCode
      });
      
      if (response.data.status === 'completed') {
        toast.success(`Joined ${response.data.organization_name} as ${response.data.role}!`);
        navigate('/dashboard');
      } else {
        toast.success('Join request submitted! Waiting for approval.');
        setStep(step + 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use code');
    } finally {
      setLoading(false);
    }
  };

  // Validate invitation
  const validateInvitation = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/invitations/by-token/${token}`);
      setInvitationDetails(response.data);
    } catch (error) {
      setInvitationDetails({ is_valid: false, error: 'Invalid invitation' });
    }
  }, []);

  // Accept invitation
  const acceptInvitation = async () => {
    if (!onboardingFlow || !invitationDetails?.is_valid) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/accept-invitation`, {
        onboarding_id: onboardingFlow.id,
        token: invitationToken
      });
      toast.success(`Joined ${response.data.organization_name} as ${response.data.role}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle user type selection
  const handleUserTypeSelect = async (type) => {
    setUserType(type);
    if (type === 'individual') {
      setStep(2);
    } else if (type === 'organization_admin') {
      setStep(2);
    } else if (type === 'staff') {
      setStep(2);
    }
  };

  // Handle individual role selection
  const handleRoleSelect = async (role) => {
    setIndividualRole(role);
    const flow = await startOnboarding('individual', role);
    if (flow) {
      setStep(3);
    }
  };

  // Handle org admin path selection
  const handleOrgAdminPath = async (path) => {
    setOrgAdminPath(path);
    const flow = await startOnboarding('organization_admin');
    if (flow) {
      setStep(3);
    }
  };

  // Handle staff path selection
  const handleStaffPath = async (path) => {
    setStaffPath(path);
    const flow = await startOnboarding('staff');
    if (flow) {
      setStep(3);
    }
  };

  // Progress indicator
  const getProgressSteps = () => {
    if (userType === 'individual') {
      return ['Select Type', 'Choose Role', 'Complete'];
    } else if (userType === 'organization_admin') {
      return ['Select Type', 'Choose Path', orgAdminPath === 'create' ? 'Create Org' : 'Join Org', 'Complete'];
    } else if (userType === 'staff') {
      return ['Select Type', 'Choose Method', 'Join Org', 'Complete'];
    }
    return ['Select Type', 'Setup', 'Complete'];
  };

  const progressSteps = getProgressSteps();

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-border bg-card-bg/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={20} className="text-white/60" />
              </button>
            )}
            <span className="font-heading text-xl font-bold">
              <span className="text-primary">MICHEZO</span>
              <span className="text-secondary">NET</span>
            </span>
          </div>
          
          {/* Progress */}
          <div className="hidden sm:flex items-center gap-2">
            {progressSteps.map((s, i) => (
              <React.Fragment key={`progress-step-${i}-${s}`}>
                <div className={`flex items-center gap-2 ${i + 1 <= step ? 'text-primary' : 'text-white/40'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    i + 1 < step ? 'bg-primary text-black' :
                    i + 1 === step ? 'border-2 border-primary text-primary' :
                    'border border-white/20 text-white/40'
                  }`}>
                    {i + 1 < step ? <Check size={14} /> : i + 1}
                  </div>
                  <span className="text-sm font-body hidden md:block">{s}</span>
                </div>
                {i < progressSteps.length - 1 && (
                  <ChevronRight size={16} className="text-white/20" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Select User Type */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                  Welcome to Michezonet
                </h1>
                <p className="text-white/60 font-body text-lg">
                  How would you like to get started?
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {USER_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <motion.button
                      key={type.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleUserTypeSelect(type.id)}
                      className="bg-card-bg border border-border rounded-xl p-6 text-left hover:border-primary/50 transition-all group"
                      data-testid={`user-type-${type.id}`}
                    >
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white mb-2">{type.title}</h3>
                      <p className="text-white/60 font-body text-sm">{type.description}</p>
                      <div className="mt-4 flex items-center gap-2 text-primary text-sm font-body">
                        <span>Get started</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 2: Individual - Select Role */}
          {step === 2 && userType === 'individual' && (
            <motion.div
              key="step2-individual"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  What best describes you?
                </h1>
                <p className="text-white/60 font-body">
                  Select your primary role in the sports community
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {config?.individual_roles?.map((role) => {
                  const Icon = ROLE_ICONS[role] || User;
                  return (
                    <motion.button
                      key={role}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRoleSelect(role)}
                      disabled={loading}
                      className="bg-card-bg border border-border rounded-xl p-5 text-center hover:border-primary/50 transition-all disabled:opacity-50"
                      data-testid={`role-${role.toLowerCase()}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <span className="font-body text-white font-medium">{role}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 2: Organization Admin - Select Path */}
          {step === 2 && userType === 'organization_admin' && (
            <motion.div
              key="step2-orgadmin"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Organization Setup
                </h1>
                <p className="text-white/60 font-body">
                  Create a new organization or join an existing one
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOrgAdminPath('create')}
                  disabled={loading}
                  className="bg-card-bg border border-border rounded-xl p-8 text-left hover:border-primary/50 transition-all disabled:opacity-50"
                  data-testid="path-create-org"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-green-600 flex items-center justify-center mb-4">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">Create Organization</h3>
                  <p className="text-white/60 font-body text-sm">
                    Register a new federation, club, academy, school, or facility
                  </p>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleOrgAdminPath('join')}
                  disabled={loading}
                  className="bg-card-bg border border-border rounded-xl p-8 text-left hover:border-secondary/50 transition-all disabled:opacity-50"
                  data-testid="path-join-org"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-secondary to-orange-600 flex items-center justify-center mb-4">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">Join Organization</h3>
                  <p className="text-white/60 font-body text-sm">
                    Request to join an existing organization as an administrator
                  </p>
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Staff - Select Method */}
          {step === 2 && userType === 'staff' && (
            <motion.div
              key="step2-staff"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Join an Organization
                </h1>
                <p className="text-white/60 font-body">
                  Choose how you&apos;d like to join
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleStaffPath('invitation')}
                  disabled={loading}
                  className="bg-card-bg border border-border rounded-xl p-6 text-left hover:border-primary/50 transition-all disabled:opacity-50"
                  data-testid="path-invitation"
                >
                  <Mail className="w-10 h-10 text-primary mb-4" />
                  <h3 className="font-heading text-lg font-bold text-white mb-2">Use Invitation</h3>
                  <p className="text-white/60 font-body text-sm">
                    I received an invitation link via email
                  </p>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleStaffPath('code')}
                  disabled={loading}
                  className="bg-card-bg border border-border rounded-xl p-6 text-left hover:border-secondary/50 transition-all disabled:opacity-50"
                  data-testid="path-access-code"
                >
                  <Key className="w-10 h-10 text-secondary mb-4" />
                  <h3 className="font-heading text-lg font-bold text-white mb-2">Enter Access Code</h3>
                  <p className="text-white/60 font-body text-sm">
                    I have an access code from my organization
                  </p>
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleStaffPath('request')}
                  disabled={loading}
                  className="bg-card-bg border border-border rounded-xl p-6 text-left hover:border-blue-500/50 transition-all disabled:opacity-50"
                  data-testid="path-request"
                >
                  <Search className="w-10 h-10 text-blue-400 mb-4" />
                  <h3 className="font-heading text-lg font-bold text-white mb-2">Request to Join</h3>
                  <p className="text-white/60 font-body text-sm">
                    Search and request to join an organization
                  </p>
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Individual - Confirmation */}
          {step === 3 && userType === 'individual' && (
            <motion.div
              key="step3-individual"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-lg mx-auto text-center"
            >
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              
              <div>
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Almost Done!
                </h1>
                <p className="text-white/60 font-body">
                  You&apos;re joining as a <span className="text-primary font-semibold">{individualRole}</span>
                </p>
              </div>

              <div className="bg-card-bg border border-border rounded-xl p-6 text-left">
                <h3 className="font-heading text-lg font-bold text-white mb-4">What you&apos;ll get:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <Check className="w-5 h-5 text-primary mt-0.5" />
                    Access to events, tournaments, and competitions
                  </li>
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <Check className="w-5 h-5 text-primary mt-0.5" />
                    Digital membership and ticketing
                  </li>
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <Check className="w-5 h-5 text-primary mt-0.5" />
                    Connect with sports organizations
                  </li>
                </ul>
              </div>

              <button
                onClick={completeIndividual}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="complete-onboarding"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>Complete Setup</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Step 3: Organization Admin - Create Org Form */}
          {step === 3 && userType === 'organization_admin' && orgAdminPath === 'create' && (
            <motion.div
              key="step3-create-org"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-xl mx-auto"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Create Your Organization
                </h1>
                <p className="text-white/60 font-body">
                  Fill in the details to register your organization
                </p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); createOrganization(); }} className="space-y-5">
                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Organization Name *</label>
                  <input
                    type="text"
                    value={orgForm.name}
                    onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                    required
                    placeholder="e.g., Nairobi Football Club"
                    className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                    data-testid="org-name"
                  />
                </div>

                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Organization Type *</label>
                  <select
                    value={orgForm.type}
                    onChange={(e) => setOrgForm({...orgForm, type: e.target.value})}
                    required
                    className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                    data-testid="org-type"
                  >
                    <option value="">Select type...</option>
                    {config?.organization_types?.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/80 font-body text-sm mb-2">Sport</label>
                    <input
                      type="text"
                      value={orgForm.sport}
                      onChange={(e) => setOrgForm({...orgForm, sport: e.target.value})}
                      placeholder="e.g., Football"
                      className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 font-body text-sm mb-2">Country</label>
                    <input
                      type="text"
                      value={orgForm.country}
                      onChange={(e) => setOrgForm({...orgForm, country: e.target.value})}
                      placeholder="e.g., Kenya"
                      className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">City</label>
                  <input
                    type="text"
                    value={orgForm.city}
                    onChange={(e) => setOrgForm({...orgForm, city: e.target.value})}
                    placeholder="e.g., Nairobi"
                    className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                  />
                </div>

                <div>
                  <label className="block text-white/80 font-body text-sm mb-2">Description</label>
                  <textarea
                    value={orgForm.description}
                    onChange={(e) => setOrgForm({...orgForm, description: e.target.value})}
                    placeholder="Tell us about your organization..."
                    rows={3}
                    className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none resize-none"
                  />
                </div>

                {orgForm.type && (
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                    <p className="text-primary text-sm font-body">
                      You will be assigned as <span className="font-bold">{orgForm.type} Admin</span>
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !orgForm.name || !orgForm.type}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="create-org-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span>Create Organization</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {/* Step 3: Organization Admin / Staff - Join Org (Search) */}
          {step === 3 && ((userType === 'organization_admin' && orgAdminPath === 'join') || (userType === 'staff' && staffPath === 'request')) && (
            <motion.div
              key="step3-join-org"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-xl mx-auto"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Find Your Organization
                </h1>
                <p className="text-white/60 font-body">
                  Search for the organization you want to join
                </p>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchOrganizations(e.target.value);
                  }}
                  placeholder="Search by organization name..."
                  className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg pl-12 pr-4 py-4 text-white font-body outline-none"
                  data-testid="org-search"
                />
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-3">
                  {searchResults.map(org => (
                    <button
                      key={org.id}
                      onClick={() => setSelectedOrg(org)}
                      className={`w-full bg-card-bg border rounded-xl p-4 text-left transition-all ${
                        selectedOrg?.id === org.id ? 'border-primary bg-primary/10' : 'border-border hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          {ORG_TYPE_ICONS[org.type] ? React.createElement(ORG_TYPE_ICONS[org.type], { className: 'w-6 h-6 text-white' }) : <Building2 className="w-6 h-6 text-white" />}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading text-white font-bold">{org.name}</h4>
                          <p className="text-white/60 text-sm">{org.type} {org.sport && `• ${org.sport}`}</p>
                        </div>
                        {selectedOrg?.id === org.id && (
                          <Check className="w-5 h-5 text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Org Form */}
              {selectedOrg && (
                <div className="space-y-4 pt-4 border-t border-border">
                  <div>
                    <label className="block text-white/80 font-body text-sm mb-2">Requested Role</label>
                    <select
                      value={requestedRole}
                      onChange={(e) => setRequestedRole(e.target.value)}
                      className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none"
                    >
                      <option value="">Select preferred role...</option>
                      {selectedOrg.available_roles?.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-white/80 font-body text-sm mb-2">Message (optional)</label>
                    <textarea
                      value={joinMessage}
                      onChange={(e) => setJoinMessage(e.target.value)}
                      placeholder="Tell them why you want to join..."
                      rows={3}
                      className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg px-4 py-3 text-white font-body outline-none resize-none"
                    />
                  </div>

                  <button
                    onClick={submitJoinRequest}
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-join-request"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <span>Submit Request</span>
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Staff - Access Code */}
          {step === 3 && userType === 'staff' && staffPath === 'code' && (
            <motion.div
              key="step3-access-code"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-md mx-auto"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Enter Access Code
                </h1>
                <p className="text-white/60 font-body">
                  Enter the code provided by your organization
                </p>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setAccessCode(code);
                      validateAccessCode(code);
                    }}
                    placeholder="Enter code (e.g., ABC123)"
                    className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg pl-12 pr-4 py-4 text-white font-body text-center text-xl tracking-wider uppercase outline-none"
                    data-testid="access-code-input"
                  />
                </div>

                {codeValidation && (
                  <div className={`rounded-lg p-4 ${codeValidation.valid ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    {codeValidation.valid ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle2 size={20} />
                          <span className="font-bold">Valid Code</span>
                        </div>
                        <p className="text-white/80 font-body">
                          Join <span className="text-white font-semibold">{codeValidation.organization_name}</span> as{' '}
                          <span className="text-primary font-semibold">{codeValidation.role}</span>
                        </p>
                        {codeValidation.requires_approval && (
                          <p className="text-yellow-400 text-sm">Requires admin approval</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <span>{codeValidation.error || 'Invalid code'}</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={useAccessCode}
                  disabled={loading || !codeValidation?.valid}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="use-access-code"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span>Join Organization</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Staff - Invitation */}
          {step === 3 && userType === 'staff' && staffPath === 'invitation' && (
            <motion.div
              key="step3-invitation"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-md mx-auto"
            >
              <div className="text-center">
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Accept Invitation
                </h1>
                <p className="text-white/60 font-body">
                  {invitationToken ? 'Review and accept your invitation' : 'Paste your invitation link or token'}
                </p>
              </div>

              {!invitationToken ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={20} />
                    <input
                      type="text"
                      value={invitationToken}
                      onChange={(e) => {
                        setInvitationToken(e.target.value);
                        validateInvitation(e.target.value);
                      }}
                      placeholder="Paste invitation token..."
                      className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg pl-12 pr-4 py-4 text-white font-body outline-none"
                      data-testid="invitation-token"
                    />
                  </div>
                </div>
              ) : invitationDetails ? (
                <div className="space-y-4">
                  {invitationDetails.is_valid ? (
                    <>
                      <div className="bg-card-bg border border-border rounded-xl p-6">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                            <Building2 className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <h3 className="font-heading text-xl font-bold text-white">{invitationDetails.organization_name}</h3>
                            <p className="text-white/60">{invitationDetails.organization_type}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between py-2 border-b border-border">
                            <span className="text-white/60">Role</span>
                            <span className="text-primary font-bold">{invitationDetails.role}</span>
                          </div>
                          {invitationDetails.message && (
                            <div className="py-2">
                              <span className="text-white/60 block mb-1">Message</span>
                              <p className="text-white">{invitationDetails.message}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={acceptInvitation}
                        disabled={loading}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="accept-invitation"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <span>Accept Invitation</span>
                            <ArrowRight size={18} />
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
                      <p className="text-red-400 font-body">
                        {invitationDetails.status === 'expired' ? 'This invitation has expired' :
                         invitationDetails.status === 'accepted' ? 'This invitation has already been used' :
                         'Invalid invitation'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              )}
            </motion.div>
          )}

          {/* Step 4: Pending Approval */}
          {step === 4 && (
            <motion.div
              key="step4-pending"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8 max-w-lg mx-auto text-center"
            >
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              
              <div>
                <h1 className="font-heading text-3xl font-bold text-white mb-3">
                  Request Submitted!
                </h1>
                <p className="text-white/60 font-body">
                  Your request is pending admin approval. You&apos;ll be notified once it&apos;s reviewed.
                </p>
              </div>

              <div className="bg-card-bg border border-border rounded-xl p-6 text-left">
                <h3 className="font-heading text-lg font-bold text-white mb-4">What happens next?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <span className="text-yellow-400 text-xs">1</span>
                    </div>
                    An administrator will review your request
                  </li>
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <span className="text-yellow-400 text-xs">2</span>
                    </div>
                    You&apos;ll receive a notification when approved
                  </li>
                  <li className="flex items-start gap-3 text-white/80 font-body">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <span className="text-yellow-400 text-xs">3</span>
                    </div>
                    Access the dashboard with your assigned role
                  </li>
                </ul>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="btn-secondary w-full"
              >
                Go to Dashboard
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
